<template>
    <div :style="{ height: height, position: 'relative' }">
        <l-map ref="map" v-model:zoom="zoom" :center="mapCenter" @ready="mapIsReady" @click="onMapClick">
            <l-tile-layer v-if="!satellite" :url="$mapTiles.street"/>
            <template v-else>
                <l-tile-layer
                    :url="$mapTiles.satellite"
                    :options="{ maxZoom: 19 }"/>
                <l-tile-layer
                    :url="$mapTiles.satelliteLabels"
                    :options="{ maxZoom: 19, pane: 'overlayPane' }"/>
            </template>
            <l-marker
                v-for="(device, index) in mappableDevices"
                :key="device.id"
                ref="markers"
                :name="`marker-${device.id}`"
                :lat-lng="[device.lat ?? 0, device.lng ?? 0]"
                @click="selectMarker(device)"
            >
                <l-icon
                    :iconSize="[36, 48]"
                    :iconAnchor="[18, 44]"
                    :popupAnchor="[0, -46]"
                    class-name="marker"
                >
                    <FleetMarker
                        :device="device"
                        :offset="markerOffset(device)"
                        :tracked="trackedDeviceIds.has(device.id)"
                        :highlighted="!!device.isHighlighted"
                    />
                </l-icon>
                <l-popup :options="{ maxWidth: 320, minWidth: 300 }">
                    <div class="popup-container">
                        <!-- Header: photo, label, subtitle, driver avatar, notification badge -->
                        <div class="popup-header">
                            <div class="popup-photo-wrapper">
                                <v-avatar size="40" rounded="lg" class="popup-photo">
                                    <v-img v-if="device.asset_photo" :src="device.asset_photo" cover/>
                                    <v-img v-else-if="device.asset_type_photo" :src="device.asset_type_photo" cover/>
                                    <v-icon v-else icon="mdi-car" size="24" color="grey"/>
                                </v-avatar>
                                <v-avatar v-if="device.driver_photo" size="20" class="popup-driver-overlay">
                                    <v-img :src="device.driver_photo" :lazy-src="avatarPlaceholder" cover/>
                                </v-avatar>
                            </div>
                            <div class="popup-header-info">
                                <router-link :to="`assets/${device.asset_id}`" class="popup-asset-label" target="_blank">
                                    {{ device.asset_label }}
                                </router-link>
                                <div class="popup-subtitle">{{ device.acquired_vin || device.vin || device.slno }}</div>
                            </div>
                            <div class="popup-header-right">
                                <v-badge v-if="device.unread_notifications" dot color="orange" offset-x="-2" offset-y="-2">
                                    <v-icon icon="mdi-bell-outline" size="18" color="grey"/>
                                </v-badge>
                            </div>
                        </div>

                        <!-- Status strip: motion pill, speed, battery/signal/voltage -->
                        <div class="popup-status-strip">
                            <span :class="['popup-motion-pill', isNonReporting(device) ? 'popup-motion-offline' : device.motion_state ? (getEffectiveSpeed(device) >= 2 ? 'popup-motion-active' : 'popup-motion-idle') : 'popup-motion-parked']">
                                <v-icon :icon="isNonReporting(device) ? 'mdi-wifi-off' : device.motion_state ? (getEffectiveSpeed(device) >= 2 ? 'mdi-navigation' : 'mdi-car-clock') : 'mdi-car-brake-parking'" size="12" color="white"/>
                                {{ motionLabel(device) }}
                            </span>
                            <span v-if="device.motion_state && getEffectiveSpeed(device) >= 2" class="popup-speed">
                                {{ strOperations().unitifyDistance(getDisplaySpeedMph(device)) }} {{ getCompassLabel(device.heading) }}
                            </span>
                            <span class="popup-status-icons">
                                <v-chip v-if="device.obd_present" size="x-small" variant="tonal" color="blue-grey" class="popup-obd-chip">OBD</v-chip>
                                <v-tooltip v-if="fuelPercent(device) != null" :text="isLowFuel(device) ? 'Low fuel' : 'Fuel level'" location="top">
                                    <template #activator="{ props: ftp }">
                                        <span v-bind="ftp" class="popup-voltage" :style="isLowFuel(device) ? 'color:#D70000;font-weight:700' : ''">
                                            <v-icon icon="mdi-gas-station" size="12" :color="isLowFuel(device) ? '#D70000' : undefined"/>{{ fuelPercent(device) }}%
                                        </span>
                                    </template>
                                </v-tooltip>
                                <v-icon v-bind="battery(device.battery_bars??0, primaryBatteryType(device))" size="14"/>
                                <v-icon v-bind="signals(device.signal_bars??0)" size="14"/>
                                <v-tooltip :text="device.obd_present ? 'Vehicle voltage' : 'Battery voltage'" location="top">
                                    <template #activator="{ props: vtp }">
                                        <span v-bind="vtp" class="popup-voltage">{{ strOperations().formatVoltage(device.voltage) }}</span>
                                    </template>
                                </v-tooltip>
                            </span>
                        </div>

                        <!-- Driver row -->
                        <div v-if="device.driver_first_name" class="popup-info-row">
                            <v-icon icon="mdi-account" size="14" color="grey" class="flex-shrink-0"/>
                            <span class="text-truncate" style="min-width: 0">{{ device.driver_first_name }} {{ device.driver_last_name }}</span>
                            <v-tooltip v-if="device.driver_score != null" text="Safety Score" location="top">
                                <template #activator="{ props: tp }">
                                    <v-chip v-bind="tp" size="x-small" variant="flat" class="ms-1 flex-shrink-0"
                                            :color="driverScoreColor(device.driver_score)">
                                        {{ device.driver_score }}
                                    </v-chip>
                                </template>
                            </v-tooltip>
                        </div>

                        <!-- Location row: zone name ("At Main Depot") as the
                             primary line when inside a named geofence, street
                             address beneath, a single icon and action buttons. -->
                        <div class="popup-info-row popup-location-row">
                            <v-icon :icon="device.current_zone ? 'mdi-map-marker-radius' : 'mdi-map-marker'" size="14" :color="device.current_zone ? 'green' : 'grey'" class="flex-shrink-0 mt-1"/>
                            <span class="popup-location-text">
                                <span v-if="device.current_zone" class="popup-zone-name">At {{ device.current_zone }}{{ !device.motion_state && !isNonReporting(device) && durationSince(device.motion_ended_at) ? ' for ' + durationSince(device.motion_ended_at) : '' }}</span>
                                <span class="popup-location-address">{{ isApproximate(device) ? 'Approximate location, waiting for first GPS fix' : (device.location ?? device.formatted_address ?? '') }}</span>
                            </span>
                            <div class="popup-location-actions">
                                <v-icon icon="mdi-content-copy" size="14" class="popup-inline-icon" title="Copy Google Maps link" @click="copyText(device)"/>
                                <v-icon icon="mdi-open-in-new" size="14" class="popup-inline-icon" title="Open in Google Maps" @click="openInGoogleMaps(device)"/>
                            </div>
                        </div>

                        <!-- Updated + status flags row -->
                        <div class="popup-info-row popup-update-row">
                            <v-icon icon="mdi-clock-outline" size="14" color="grey"/>
                            <span>{{ tickingTimeAgo(device.last_message_received) }}</span>
                            <template v-if="deviceStatusFlags[device.asset_id]">
                                <v-tooltip v-if="deviceStatusFlags[device.asset_id].checkEngine" text="Check Engine - View DTCs" location="top">
                                    <template #activator="{ props: tp }">
                                        <router-link :to="{path: `assets/${device.asset_id}`, query: {tab: 4}}" target="_blank">
                                            <v-icon v-bind="tp" icon="mdi-engine" color="orange" size="14"/>
                                        </router-link>
                                    </template>
                                </v-tooltip>
                                <v-tooltip v-if="deviceStatusFlags[device.asset_id].inspectionDue" text="Inspection Due" location="top">
                                    <template #activator="{ props: tp }">
                                        <router-link :to="{path: `assets/${device.asset_id}`, query: {tab: 0}}" target="_blank">
                                            <v-icon v-bind="tp" icon="mdi-clipboard-check-outline" color="orange" size="14"/>
                                        </router-link>
                                    </template>
                                </v-tooltip>
                                <v-tooltip v-if="deviceStatusFlags[device.asset_id].serviceDue" text="Service Due" location="top">
                                    <template #activator="{ props: tp }">
                                        <router-link :to="{path: `assets/${device.asset_id}`, query: {tab: 1}}" target="_blank">
                                            <v-icon v-bind="tp" icon="mdi-wrench-clock" color="orange" size="14"/>
                                        </router-link>
                                    </template>
                                </v-tooltip>
                                <v-tooltip v-if="deviceStatusFlags[device.asset_id].noDriver" text="No Driver Assigned" location="top">
                                    <template #activator="{ props: tp }">
                                        <v-icon v-bind="tp" icon="mdi-account-off" color="orange" size="14"/>
                                    </template>
                                </v-tooltip>
                            </template>
                        </div>

                        <v-divider class="my-2"/>

                        <!-- Action buttons -->
                        <div class="popup-actions">
                            <div class="popup-action-btn" @click="openAssetDetails(device.asset_id)">
                                <span class="popup-action-circle" style="background: #1565C0">
                                    <v-icon icon="mdi-information-outline" size="18" color="white"/>
                                </span>
                                <span class="popup-action-label">Details</span>
                            </div>
                            <div class="popup-action-btn" @click="toggleTrackDevice(device.id)">
                                <span class="popup-action-circle" :style="{ background: trackedDeviceIds.has(device.id) ? '#C62828' : '#00897B' }">
                                    <v-icon icon="mdi-radar" size="18" color="white"/>
                                </span>
                                <span class="popup-action-label">{{ trackedDeviceIds.has(device.id) ? 'Unfollow' : 'Follow' }}</span>
                            </div>
                            <div class="popup-action-btn" @click="openAssetTrips(device.asset_id)">
                                <span class="popup-action-circle" style="background: #1565C0">
                                    <v-icon icon="mdi-routes" size="18" color="white"/>
                                </span>
                                <span class="popup-action-label">Trips</span>
                            </div>
                            <div class="popup-action-btn" @click="startLiveTracking(device.id)">
                                <span class="popup-action-circle" :style="{ background: liveTrackDeviceIds.has(device.id) ? '#B71C1C' : '#6A1B9A' }">
                                    <v-icon :icon="liveTrackDeviceIds.has(device.id) ? 'mdi-access-point' : 'mdi-access-point-off'" size="18" color="white"/>
                                </span>
                                <span class="popup-action-label">{{ liveTrackDeviceIds.has(device.id) ? 'Tracking' : 'Live' }}</span>
                            </div>
                            <div class="popup-action-btn" @click="openStreetView(device)">
                                <span class="popup-action-circle" style="background: #2E7D32">
                                    <v-icon icon="mdi-google-street-view" size="18" color="white"/>
                                </span>
                                <span class="popup-action-label">Street View</span>
                            </div>
                            <div class="popup-action-btn" @click="openDeviceAlerts(device.asset_id)">
                                <span class="popup-action-circle" :style="{ background: device.unread_notifications ? '#EF6C00' : '#757575' }">
                                    <v-icon :icon="device.unread_notifications ? 'mdi-bell-badge' : 'mdi-bell-outline'" size="18" color="white"/>
                                </span>
                                <span class="popup-action-label">Alerts</span>
                            </div>
                        </div>
                    </div>
                </l-popup>
            </l-marker>
            <!-- Address labels for followed devices -->
            <l-marker
                v-for="label in followedDeviceLabels"
                :key="label.key"
                :lat-lng="label.latLng"
                :options="{ interactive: true, zIndexOffset: -1000 }"
            >
                <l-icon
                    :iconSize="[230, 60]"
                    :iconAnchor="[115, -20]"
                    class-name="address-label-marker"
                >
                    <div class="address-label-content">
                        <div v-if="label.assetName" class="asset-name-label">
                            <span v-if="label.isLiveTracking" class="live-badge">LIVE</span>
                            {{ label.assetName }}
                        </div>
                        <div v-if="label.speedInfo" class="speed-info" :style="{ color: label.speedColor }">
                            <v-icon :icon="label.speedIcon" size="10" :color="label.speedColor"/>
                            {{ label.speedInfo }}
                            <span v-if="label.timeAgo" class="time-ago">· {{ label.timeAgo }}</span>
                        </div>
                        <div v-if="label.address" class="address-text">
                            {{ label.address }}
                            <v-icon
                                icon="mdi-content-copy"
                                size="12"
                                color="white"
                                class="copy-address-icon"
                                title="Copy Google Maps link"
                                @click.stop="copyToClipboard(`https://www.google.com/maps?q=${label.lat},${label.lng}`)"
                            />
                        </div>
                    </div>
                </l-icon>
            </l-marker>
        </l-map>
        <!-- Zones & spots overlay toggle (company users only); opt-in, default off -->
        <v-btn v-if="user && user.type === 'company'" icon size="small"
               style="position:absolute;top:12px;right:12px;z-index:500"
               :color="showZonesOverlay ? 'primary' : undefined"
               @click="toggleZonesOverlay">
            <v-icon>mdi-select-group</v-icon>
            <v-tooltip activator="parent" location="left">
                {{ showZonesOverlay ? 'Hide zones & spots' : 'Show zones & spots' }}
            </v-tooltip>
        </v-btn>
        <!-- Following devices panel -->
        <div v-if="trackedDeviceIds.size" class="following-panel" @click.stop>
            <v-chip
                color="primary"
                variant="flat"
                size="small"
                prepend-icon="mdi-track-changes"
                @click="showFollowingPanel = !showFollowingPanel"
            >
                Following: {{ trackedDeviceIds.size }}
                <v-icon :icon="showFollowingPanel ? 'mdi-chevron-down' : 'mdi-chevron-up'" size="small" class="ms-1"/>
            </v-chip>
            <div v-if="showFollowingPanel" class="following-list">
                <div v-for="device in followedDevicesList" :key="device.id" class="following-item">
                    <div class="following-item-header">
                        <v-icon :icon="'mdi-map-marker'" :color="device.asset_color || device.status_color" size="14"/>
                        <span class="following-label">{{ device.asset_label || device.slno }}</span>
                        <v-icon
                            :icon="liveTrackDeviceIds.has(device.id) ? 'mdi-access-point' : 'mdi-access-point-off'"
                            :color="liveTrackDeviceIds.has(device.id) ? 'red' : 'grey'"
                            size="14"
                            class="cursor-pointer mr-1"
                            :title="liveTrackDeviceIds.has(device.id) ? 'Live tracking active' : 'Request live updates'"
                            @click.stop="!liveTrackDeviceIds.has(device.id) && startLiveTracking(device.id)"
                        />
                        <v-icon icon="mdi-close-circle" size="14" class="unfollow-btn" @click="toggleTrackDevice(device.id)"/>
                    </div>
                    <div v-if="device.formatted_address || device.location" class="following-address">
                        <span>{{ (device.formatted_address || device.location).substring(0, 45) }}{{ (device.formatted_address || device.location).length > 45 ? '...' : '' }}</span>
                        <v-icon icon="mdi-content-copy" size="10" class="copy-address-icon" @click.stop="copyToClipboard(`https://www.google.com/maps?q=${device.lat},${device.lng}`)"/>
                    </div>
                </div>
                <v-btn v-if="trackedDeviceIds.size > 1" size="x-small" variant="text" color="error" block class="mt-1" @click="unfollowAll">
                    Unfollow All
                </v-btn>
            </div>
        </div>
        <!-- Torch beams -->
        <TorchBeamLayer :devices="devicesInMotion" :is-map-ready="mapState" :map-ref="map"/>
    </div>
</template>

<script setup>
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import {computed, inject, onBeforeUnmount, ref, watch} from "vue";
import {strOperations} from "@/composables/globals";
import {useClipboard} from "@/composables/clipboard";
import {geocodeLatLng, isValidAddress} from "@/composables/useGeocode";
import avatarPlaceholder from "@/assets/images/avatar.png";
import TorchBeamLayer from "@/components/cards/devices/TorchBeamLayer.vue";
import FleetMarker from "@/components/cards/devices/FleetMarker.vue";
import {useBeepSounds} from "@/composables/useBeepSounds";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {LIcon, LMap, LMarker, LPopup, LTileLayer} from "@vue-leaflet/vue-leaflet";
import {powerSourceIcon, primaryBatteryType} from "@/composables/usePowerSourceIcon";
import {getStoppedLabel, hasMappableCoords, isApproximate, isNeverReported, isNonReporting, isShownOnMap, fuelPercent, isLowFuel} from "@/composables/fleetStatus";

const {showSnackbar} = useSnackbarStore();
const map = ref();
const zoom = ref(2);
const bounds = ref([]);
const markers = ref([]);
const mapState = ref([]);
const {user} = useAuthStore();
const companyLocations = ref([]);
const selectedMarker = ref(null);
const trackedDeviceIds = ref(new Set());
const {copyToClipboard} = useClipboard();
const {playMotionStarted, playMotionChirp, playMotionEnded, dispose: disposeBeepSounds} = useBeepSounds();
const locationPolygons = ref(new L.FeatureGroup());
// Live zones/spots overlay: company zones + saved spots drawn on the live map,
// opt-in via a toggle and fetched from GET companies/{company}/zones-overlay
// (cached on zones_updated_at). Separate from the coarse company-location
// polygons above. Default off, so the map's default behaviour is unchanged.
const zonesOverlay = ref(new L.FeatureGroup());
const showZonesOverlay = ref(false);
const zonesOverlayLoadedAt = ref(null); // the zones_updated_at we last drew
let zonesOverlayLoading = false;
// Zone fill by zone.type, mirroring DevicesLotView's ZONE_COLORS.
const ZONE_OVERLAY_COLORS = {
    0: '#4CAF50', 1: '#2196F3', 2: '#795548', 3: '#FF9800',
    4: '#9C27B0', 5: '#00BCD4', 6: '#673AB7',
};
const mapCenter = ref([47.41322, -1.219482]);
const lastMqttUpdate = inject('lastMqttUpdate', ref(null));

// Device position animation state
const deviceAnimations = new Map(); // serial → animation data
let animationTimer = null;
const ANIM_STEPS = 10;
const ANIM_STEP_MS = 50; // 10 × 50ms = 500ms total

// Movement trail state
const deviceTrails = new Map(); // serial → trail data
let trailCleanupTimer = null;
const TRAIL_SOLID_MS = 3000;
const TRAIL_FADE_STEPS = 10;
const TRAIL_FADE_STEP_MS = 50;

// Followed device address labels
const followedDeviceAddresses = ref(new Map()); // serial → address string
const lastGeocodedPositions = ref(new Map()); // serial → {lat, lng}
const showFollowingPanel = ref(false);

// Time ago ticking - forces followedDeviceLabels recomputation every 5s
const timeAgoTick = ref(0);
let timeAgoTimer = null;

// Live tracking state
const liveTrackDeviceIds = ref(new Set());
const liveTrackExpiry = ref(new Map()); // deviceId → expiry timestamp (ms)
const props = defineProps(['devices', 'allDevices', 'height', 'selectedDevice', 'zoomToDevice', 'nearestToDevice', 'companyLocations', 'serverFleetCounts', 'satellite']);

// Merge followed devices back into the rendered list so they're never filtered out
const devicesWithFollowed = computed(() => {
    if (!trackedDeviceIds.value.size || !props.allDevices) return props.devices;
    const filteredIds = new Set(props.devices.map(d => d.id));
    const missingFollowed = props.allDevices.filter(d => trackedDeviceIds.value.has(d.id) && !filteredIds.has(d.id));
    if (!missingFollowed.length) return props.devices;
    return [...props.devices, ...missingFollowed];
});

const mappableDevices = computed(() => devicesWithFollowed.value.filter(isShownOnMap));
const emits = defineEmits(['selectedMarker', 'mapClicked', 'devicesStale']);

const serverAssetIdSets = computed(() => {
    const map = {};
    (props.serverFleetCounts || []).forEach(item => {
        if (item.asset_ids) {
            map[item.key] = new Set(item.asset_ids);
        }
    });
    return map;
});

const deviceStatusFlags = computed(() => {
    const flags = {};
    (props.devices || []).forEach(device => {
        const id = device.asset_id;
        flags[id] = {
            checkEngine: !!serverAssetIdSets.value.check_engine?.has(id) || !!device.indication_lamp_status,
            inspectionDue: !!serverAssetIdSets.value.pending_inspection?.has(id),
            serviceDue: !!serverAssetIdSets.value.pending_maintenance?.has(id),
            noDriver: !device.driver_first_name,
        };
    });
    return flags;
});

const mqttMessageTypes = {
    MOTION_STARTED: 2,
    MOTION_IN_PROGRESS: 3,
    MOTION_ENDED: 4,
}

const devicesInMotion = computed(() => {
    return props.devices.filter((device) => device['motion_state'] === 1 && device['isHighlighted'] === 1)
})

const getDeviceMarker = (deviceID) => {
    return markers.value.find((marker) => marker.name === `marker-${deviceID}`);
}
const highLightMarkers = (deviceIDs) => {
    props.devices.map((device) =>
        deviceIDs.includes(device.id) ? device.isHighlighted = true : device.isHighlighted = false
    );
}
const findNearestMarker = (selectedMarker) => {
    let minDistance = Infinity;
    let nearestMarker = null;

    markers.value.forEach((marker) => {
        if (marker.name !== selectedMarker.name && marker.latLng) {
            const distance = L.latLng(selectedMarker.latLng).distanceTo(L.latLng(marker.latLng));
            if (distance < minDistance) {
                minDistance = distance;
                nearestMarker = marker;
            }
        }
    });

    if (nearestMarker) {
        highLightMarkers([props.nearestToDevice.id, parseInt(nearestMarker.name.replace('marker-', ''))]);
        bounds.value = L.featureGroup([L.marker(selectedMarker.latLng), L.marker(nearestMarker.latLng)]).getBounds();
    }
}
const signals = (strength) => {
    switch (strength) {
        case 0:
            return {
                icon: 'mdi-signal-off',
                color: '#dbdbdb'
            }
        case 1:
            return {
                icon: 'mdi-signal-cellular-outline',
                color: '#d70000'
            }
        case 2:
            return {
                icon: 'mdi-signal-cellular-1',
                color: '#ff9e01'
            }
        case 3:
            return {
                icon: 'mdi-signal-cellular-2',
                color: '#849e00'
            }
        case 4:
            return {
                icon: 'mdi-signal-cellular-3',
                color: '#099f00'
            }
        case 5:
            return {
                icon: 'mdi-signal-cellular-3',
                color: '#099f00'
            }
    }
}
const battery = (strength, type = null) => {
    return powerSourceIcon(strength, type)
}
const driverScoreColor = (score) => {
    if (score >= 8.0) return 'success'
    if (score >= 5.0) return 'warning'
    return 'error'
}
const copyText = (device) => {
    if (device && device.lat && device.lng) {
        copyToClipboard(`https://www.google.com/maps?q=${device.lat},${device.lng}`);
    } else if (device && device.formatted_address && device.formatted_address !== 'Address not found') {
        copyToClipboard(device.formatted_address);
    } else {
        copyToClipboard(`${device.lat}, ${device.lng}`);
    }
}
const updateGeocodedAddress = (address) => {
    send("put", `devices/update-address`, {device: selectedMarker.value.id, address: address})
        .catch((errorResponse) => console.log(errorResponse));
}
const openStreetView = (device) => {
    window.open(
        `http://maps.google.com/maps?q=${device.lat},${device.lng}&layer=t&cbll=${device.lat},${device.lng}&cbp=12,0,0,0,0&output=classic`,
        "_blank"
    );
}
const openInGoogleMaps = (device) => {
    window.open(`https://www.google.com/maps?q=${device.lat},${device.lng}`, '_blank');
}
const openAssetDetails = (assetId) => {
    window.open(`/assets/${assetId}`, '_blank');
}
const openAssetTrips = (assetId) => {
    window.open(`/assets/${assetId}?tab=5`, '_blank');
}
const openDeviceAlerts = (assetId) => {
    window.open(`/notifications?asset=${assetId}&unread=1`, '_blank');
}
const checkLastMessage = () => {
    const now = new Date();
    let changed = false;

    props.devices.forEach((device, index) => {
        let ts = String(device.last_message_received || '');
        if (ts && !ts.endsWith('Z') && !ts.includes('+') && !ts.includes('T')) {
            ts = ts.replace(' ', 'T') + 'Z';
        }
        const lastReceived = new Date(ts);
        const diffInMinutes = (now - lastReceived) / 1000 / 60;
        if (!isNaN(diffInMinutes) && diffInMinutes > 3 && device.motion_state !== 0) {
            props.devices[index]['isHighlighted'] = 0;
            props.devices[index]['motion_state'] = 0;
            props.devices[index]['status_color'] = 'amber';
            delete props.devices[index]['fleet_status'];
            changed = true;
        }
    });
    if (changed) emits('devicesStale');
}

// --- Position Animation Helpers ---
const getAnimatedPosition = (anim) => {
    const elapsed = Date.now() - anim.startTime;
    const step = Math.min(Math.floor(elapsed / ANIM_STEP_MS), ANIM_STEPS);
    if (step >= ANIM_STEPS) return { lat: anim.endLat, lng: anim.endLng };
    const t = step / ANIM_STEPS;
    // Coerced HERE as well as where the animation is created. This single
    // expression is the one that manufactures a corrupt coordinate: `startLat`
    // as a string makes `string + number` a CONCATENATION, so a position
    // becomes "34.0785081-0.00022284" and grows by one step every tick. It
    // reaches Leaflet as an Invalid LatLng and takes the map's redraw with it.
    // The guard at the call site cannot cover an animation chained from an
    // earlier one, which is how a single string survives many updates.
    const startLat = Number(anim.startLat);
    const startLng = Number(anim.startLng);
    return {
        lat: startLat + (Number(anim.endLat) - startLat) * t,
        lng: startLng + (Number(anim.endLng) - startLng) * t,
    };
}

const isAnimDone = (anim) => {
    return (Date.now() - anim.startTime) >= (ANIM_STEPS * ANIM_STEP_MS);
}

const startDeviceAnimation = (serial, oldLat, oldLng, newLat, newLng, deviceIndex) => {
    // Ensure numeric values to prevent string concatenation in interpolation
    const nOldLat = Number(oldLat), nOldLng = Number(oldLng), nNewLat = Number(newLat), nNewLng = Number(newLng);
    // Chain from current position if animation is in progress
    const existing = deviceAnimations.get(serial);
    if (existing && !isAnimDone(existing)) {
        const pos = getAnimatedPosition(existing);
        deviceAnimations.set(serial, { startLat: pos.lat, startLng: pos.lng, endLat: nNewLat, endLng: nNewLng, startTime: Date.now(), deviceIndex });
    } else {
        deviceAnimations.set(serial, { startLat: nOldLat, startLng: nOldLng, endLat: nNewLat, endLng: nNewLng, startTime: Date.now(), deviceIndex });
    }
    // Start trail line
    startTrail(serial, oldLat, oldLng, newLat, newLng, deviceIndex);
    // Start shared timer if not running
    if (!animationTimer) {
        animationTimer = setInterval(tickAnimations, ANIM_STEP_MS);
    }
}

const tickAnimations = () => {
    const completed = [];
    const animList = props.allDevices || props.devices;
    for (const [serial, anim] of deviceAnimations.entries()) {
        if (isAnimDone(anim)) {
            const idx = animList.findIndex(d => d.slno === serial);
            if (idx > -1) {
                animList[idx].lat = anim.endLat;
                animList[idx].lng = anim.endLng;
            }
            completed.push(serial);
        } else {
            const pos = getAnimatedPosition(anim);
            const idx = animList.findIndex(d => d.slno === serial);
            if (idx > -1) {
                animList[idx].lat = pos.lat;
                animList[idx].lng = pos.lng;
            }
        }
    }
    completed.forEach(s => deviceAnimations.delete(s));
    if (deviceAnimations.size === 0) {
        clearInterval(animationTimer);
        animationTimer = null;
    }
}

// --- Trail Line Helpers ---
const startTrail = (serial, oldLat, oldLng, newLat, newLng, deviceIndex) => {
    const existing = deviceTrails.get(serial);
    if (existing?.polyline) {
        existing.polyline.remove();
        if (existing.fadeTimer) clearInterval(existing.fadeTimer);
    }

    if (!map.value?.leafletObject) return;

    const trailList = props.allDevices || props.devices;
    const color = trailList[deviceIndex]?.asset_color || trailList[deviceIndex]?.status_color || '#333333';
    const polyline = L.polyline(
        [[oldLat, oldLng], [newLat, newLng]],
        { color, weight: 3, opacity: 0.8 }
    ).addTo(map.value.leafletObject);

    deviceTrails.set(serial, {
        polyline,
        from: [oldLat, oldLng],
        to: [newLat, newLng],
        color,
        createdAt: Date.now(),
        fadeTimer: null,
    });

    if (!trailCleanupTimer) {
        trailCleanupTimer = setInterval(tickTrails, TRAIL_FADE_STEP_MS);
    }
}

const tickTrails = () => {
    const now = Date.now();
    const toDelete = [];

    for (const [serial, trail] of deviceTrails.entries()) {
        const elapsed = now - trail.createdAt;
        const totalDuration = TRAIL_SOLID_MS + (TRAIL_FADE_STEPS * TRAIL_FADE_STEP_MS);

        if (elapsed >= totalDuration) {
            trail.polyline.remove();
            toDelete.push(serial);
        } else if (elapsed >= TRAIL_SOLID_MS) {
            const fadeElapsed = elapsed - TRAIL_SOLID_MS;
            const fadeProgress = Math.min(fadeElapsed / (TRAIL_FADE_STEPS * TRAIL_FADE_STEP_MS), 1.0);
            const opacity = (1.0 - fadeProgress) * 0.8;

            const newStartLat = Number(trail.from[0]) + (Number(trail.to[0]) - Number(trail.from[0])) * fadeProgress;
            const newStartLng = Number(trail.from[1]) + (Number(trail.to[1]) - Number(trail.from[1])) * fadeProgress;

            trail.polyline.setLatLngs([[newStartLat, newStartLng], trail.to]);
            trail.polyline.setStyle({ opacity });
        }
    }

    toDelete.forEach(s => deviceTrails.delete(s));

    if (deviceTrails.size === 0) {
        clearInterval(trailCleanupTimer);
        trailCleanupTimer = null;
    }
}

// --- Compass & Geocoding Helpers ---
const getCompassLabel = (heading) => {
    const compass = (heading || 0) % 360;
    const directions = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
    return directions[Math.round(compass / 45) % 8];
}

// Effective speed: max(gps, obd) when device has OBD interface, raw number.
//
// Deliberately still the RAW column. Every moving/idle boundary in this file
// compares against it, and the server's own thresholds (DeviceLiveViewResource,
// AssetController, DeviceFleetStatus) compare against the raw column too. Moving
// this to converted MPH without moving those together would make the chips
// disagree with the counts.
const getEffectiveSpeed = (device) => Math.max(device.gps_speed_raw ?? device.gps_speed ?? 0, device.obd_present ? (device.obd_speed ?? 0) : 0);

// What to SHOW the user, in MPH.
//
// gps_speed carries two scales - Orion hardware writes MPH, the phone app
// writes km/h x2 - so the raw number is not displayable on its own. The server
// resolves the device family and sends speed_mph; prefer it. Fall back to the
// raw value, which is exactly what was displayed before, so an older API or an
// un-migrated gateway keeps behaving as it does today.
// speed_mph covers gps_speed ONLY, so the OBD half has to be folded back in or
// an OBD device would display its GPS speed while its status pill still reflects
// max(gps, obd).
const getDisplaySpeedMph = (device) => Math.max(
    strOperations().speedToMph(device, device.gps_speed_raw ?? device.gps_speed ?? 0),
    device.obd_present ? (device.obd_speed ?? 0) : 0
);

// Ticking time-ago for template use (references timeAgoTick to force re-render)
const tickingTimeAgo = (lastMessageReceived) => {
    void timeAgoTick.value; // reactive dependency
    return getTimeAgo(lastMessageReceived);
}
const getTimeAgo = (lastMessageReceived) => {
    if (!lastMessageReceived) return '';
    const now = new Date();
    // API timestamps are UTC but lack timezone suffix - ensure parsed as UTC
    let tsStr = String(lastMessageReceived);
    if (!tsStr.endsWith('Z') && !tsStr.includes('+') && !tsStr.includes('T')) {
        tsStr = tsStr.replace(' ', 'T') + 'Z';
    }
    const last = new Date(tsStr);
    const diffMs = now - last;
    if (isNaN(diffMs) || diffMs < 0) return '';
    const diffSecs = Math.floor(diffMs / 1000);
    if (diffSecs < 5) return 'just now';
    if (diffSecs < 60) return `${diffSecs}s ago`;
    const diffMins = Math.floor(diffSecs / 60);
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${Math.floor(diffHours / 24)}d ago`;
}

// Compact "how long since <ts>" for the motion pill: parked time from
// motion_ended_at, moving time from motion_started_at. Mirrors the mobile app's
// durationSinceLabel. Returns '' when the value is absent/unparseable.
const durationSince = (raw) => {
    void timeAgoTick.value; // reactive dependency so it ticks with the timer
    if (!raw) return '';
    let s = String(raw).trim();
    if (!s) return '';
    s = s.replace(' ', 'T');
    if (!s.endsWith('Z') && !/[+-]\d\d:?\d\d$/.test(s)) s += 'Z';
    const diffMs = new Date() - new Date(s);
    if (isNaN(diffMs) || diffMs < 0) return '';
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) {
        const rm = mins % 60;
        return rm > 0 ? `${hours}h ${rm}m` : `${hours}h`;
    }
    const days = Math.floor(hours / 24);
    const rh = hours % 24;
    return rh > 0 ? `${days}d ${rh}h` : `${days}d`;
}

// Motion pill text with how long the device has been in its current state, e.g.
// "In Motion for 2h", "Idling for 15m", "Parked for 3d 4h", "Offline for 2d".
const motionLabel = (device) => {
    if (isNeverReported(device)) return 'Not yet reporting';
    if (isNonReporting(device)) {
        const since = durationSince(device.last_message_received);
        if (!since) return 'Offline';
        return since === 'just now' ? 'Offline just now' : `Offline for ${since}`;
    }
    const base = device.motion_state
        ? (getEffectiveSpeed(device) >= 2 ? 'In Motion' : 'Idling')
        : getStoppedLabel(device);
    const since = durationSince(device.motion_state ? device.motion_started_at : device.motion_ended_at);
    if (!since) return base;
    return since === 'just now' ? `${base} just now` : `${base} for ${since}`;
}


let lastGeocodeTime = 0;
const GEOCODE_MIN_INTERVAL_MS = 1100; // Nominatim: max 1 req/sec

const geocodeFollowedDevice = async (serial, lat, lng) => {
    const lastPos = lastGeocodedPositions.value.get(serial);
    if (lastPos) {
        const dist = L.latLng(lastPos.lat, lastPos.lng).distanceTo(L.latLng(lat, lng));
        if (dist < 100) return;
    }

    const now = Date.now();
    const timeSinceLast = now - lastGeocodeTime;
    if (timeSinceLast < GEOCODE_MIN_INTERVAL_MS) {
        await new Promise(r => setTimeout(r, GEOCODE_MIN_INTERVAL_MS - timeSinceLast));
    }
    lastGeocodeTime = Date.now();

    lastGeocodedPositions.value.set(serial, { lat, lng });

    try {
        const address = await geocodeLatLng({ lat, lon: lng });
        if (isValidAddress(address)) {
            // Create a new Map to trigger Vue reactivity
            const newMap = new Map(followedDeviceAddresses.value);
            newMap.set(serial, address);
            followedDeviceAddresses.value = newMap;
            const idx = props.devices.findIndex(d => d.slno === serial);
            if (idx > -1) {
                props.devices[idx].formatted_address = address;
                props.devices[idx].location = address;
            }
        }
    } catch (e) {
        console.warn(`Geocode error for ${serial}:`, e);
    }
}

// --- Address Label Computed ---
const followedDeviceLabels = computed(() => {
    // Reference timeAgoTick to force recomputation every 5s
    void timeAgoTick.value;
    if (!trackedDeviceIds.value.size) return [];
    const labels = [];
    for (const device of (props.allDevices || props.devices)) {
        if (!trackedDeviceIds.value.has(device.id)) continue;
        const serial = device.slno;
        if (!serial || !device.lat || !device.lng) continue;

        const geocoded = followedDeviceAddresses.value.get(serial);
        const devAddr = (device.formatted_address && device.formatted_address !== 'Address not found') ? device.formatted_address : null;
        const devLoc = (device.location && device.location !== 'Address not found') ? device.location : null;
        const address = geocoded || devAddr || devLoc || '';
        const isDeviceMoving = device.motion_state === 1;
        let speedInfo = '', speedColor = '', speedIcon = '';

        const effectiveSpeed = getEffectiveSpeed(device);
        if (isDeviceMoving && effectiveSpeed >= 2) {
            speedInfo = `${strOperations().unitifyDistance(getDisplaySpeedMph(device))} ${getCompassLabel(device.heading)}`;
            speedColor = '#69f0ae';
            speedIcon = 'mdi-speedometer';
        } else if (device.motion_state === 1) {
            speedInfo = 'Idling';
            speedColor = '#ffca28';
            speedIcon = 'mdi-car-clock';
        } else {
            speedInfo = getStoppedLabel(device);
            speedColor = '#9e9e9e';
            speedIcon = 'mdi-car-brake-parking';
        }

        labels.push({
            key: `addr_${serial}`,
            deviceId: device.id,
            assetName: device.asset_label || device.slno,
            latLng: [device.lat, device.lng],
            address: address.length > 50 ? address.substring(0, 50) + '...' : address,
            fullAddress: address,
            lat: device.lat,
            lng: device.lng,
            speedInfo,
            speedColor,
            speedIcon,
            timeAgo: getTimeAgo(device.last_message_received),
            isLiveTracking: liveTrackDeviceIds.value.has(device.id),
        });
    }
    return labels;
});

const followedDevicesList = computed(() => {
    return (props.allDevices || props.devices).filter(d => trackedDeviceIds.value.has(d.id));
});

const unfollowAll = () => {
    for (const device of followedDevicesList.value) {
        if (device.slno) {
            followedDeviceAddresses.value.delete(device.slno);
            lastGeocodedPositions.value.delete(device.slno);
        }
    }
    trackedDeviceIds.value = new Set();
    showFollowingPanel.value = false;
    showSnackbar('Unfollowed all devices', 'info', 2000);
};

const startLiveTracking = async (deviceId) => {
    if (liveTrackDeviceIds.value.has(deviceId)) {
        showSnackbar('Live tracking already active', 'info', 2000);
        return;
    }
    try {
        const response = await send('post', `devices/${deviceId}/live-track`);
        if (response?.data?.success) {
            liveTrackDeviceIds.value.add(deviceId);
            liveTrackExpiry.value.set(deviceId, Date.now() + 10 * 60 * 1000);
            showSnackbar('Live tracking requested', 'success', 2000);
        }
    } catch (e) {
        if (e?.response?.status === 429) {
            showSnackbar('Please wait before requesting again', 'warning', 3000);
        } else {
            showSnackbar('Failed to start live tracking', 'error', 3000);
        }
    }
};

// Tick counter drives reactive recomputation of tickingTimeAgo() in templates
// Also sweeps expired live tracking entries
timeAgoTimer = setInterval(() => {
    timeAgoTick.value++;
    // Sweep expired live tracking entries
    const now = Date.now();
    for (const [deviceId, expiry] of liveTrackExpiry.value) {
        if (expiry < now) {
            liveTrackDeviceIds.value.delete(deviceId);
            liveTrackExpiry.value.delete(deviceId);
        }
    }
}, 5000);

const selectMarker = (device) => {
    // Pause animation timer while popup is open
    if (animationTimer) {
        clearInterval(animationTimer);
        animationTimer = null;
        for (const [serial, anim] of deviceAnimations.entries()) {
            const idx = props.devices.findIndex(d => d.slno === serial);
            if (idx > -1) {
                props.devices[idx].lat = anim.endLat;
                props.devices[idx].lng = anim.endLng;
            }
        }
        deviceAnimations.clear();
    }
    if (selectedMarker.value?.id !== device.id) {
        selectedMarker.value = device;
        emits('selectedMarker', device);
    }
}
const toggleTrackDevice = (deviceId) => {
    const newSet = new Set(trackedDeviceIds.value);
    const device = (props.allDevices || props.devices).find(d => d.id === deviceId);
    const label = device?.asset_label || device?.slno || 'device';
    if (newSet.has(deviceId)) {
        newSet.delete(deviceId);
        // Clean up address label data for unfollowed device
        if (device?.slno) {
            followedDeviceAddresses.value.delete(device.slno);
            lastGeocodedPositions.value.delete(device.slno);
        }
        showSnackbar(`Unfollowed ${label}`, 'info', 2000);
    } else {
        newSet.add(deviceId);
        // Trigger initial geocode for newly followed device. Not for an
        // approximate seed: geocoding it would label a point it never reported from.
        if (device?.slno && device.lat && device.lng && !isApproximate(device)) {
            geocodeFollowedDevice(device.slno, device.lat, device.lng);
        }
        showSnackbar(`Following ${label}`, 'success', 2000);
    }
    trackedDeviceIds.value = newSet;
    fitTrackedDevices();
}
const fitTrackedDevices = () => {
    if (!trackedDeviceIds.value.size || !map.value?.leafletObject) return;
    const latLngs = props.devices
        .filter(d => trackedDeviceIds.value.has(d.id) && isShownOnMap(d))
        .map(d => [d.lat, d.lng]);
    if (latLngs.length) {
        map.value.leafletObject.fitBounds(L.latLngBounds(latLngs), {padding: [80, 80], maxZoom: 18});
    }
}

// Run every 30 seconds to detect 3-minute staleness
const lastMessageCheckTimer = setInterval(checkLastMessage, 30 * 1000);

const onMapClick = () => {
    showFollowingPanel.value = false;
    emits('mapClicked');
};

// ===== Collision declutter =====
const COLLISION_ENABLED = true;
const COLLISION_MIN_DIST = 52;
const ZERO_OFFSET = {x: 0, y: 0};
const markerOffsets = ref({});
const markerOffset = (device) => (COLLISION_ENABLED && markerOffsets.value[device.id]) || ZERO_OFFSET;
// Highest priority anchors the true point; lower ones get nudged away.
const markerPriority = (d) => {
    if (props.selectedDevice && props.selectedDevice.id === d.id) return 1000;
    if (trackedDeviceIds.value.has(d.id)) return 900;
    if (d.motion_state) return 600;
    return 100;
};
let collideTimer = null;
let mapMoving = false;
const scheduleCollision = () => {
    if (mapMoving) return; // suppress recomputes while the map is in motion
    if (collideTimer) clearTimeout(collideTimer);
    collideTimer = setTimeout(recomputeCollisions, 150);
};
const declutterGroup = (group, offsets) => {
    const sorted = [...group].sort((a, b) => b.priority - a.priority);
    const placed = [];
    sorted.forEach((m, idx) => {
        let tries = 0;
        while (!m.moving && tries < 12) {
            const hit = placed.find((p) => Math.hypot(p.x - m.x, p.y - m.y) < COLLISION_MIN_DIST);
            if (!hit) break;
            let dx = m.x - hit.x;
            let dy = m.y - hit.y;
            let dist = Math.hypot(dx, dy);
            if (dist < 0.5) {
                const ang = idx * 0.9; // exactly stacked: deterministic fan-out
                dx = Math.cos(ang);
                dy = Math.sin(ang);
                dist = 1;
            }
            const step = (COLLISION_MIN_DIST - dist) + 6;
            m.x += (dx / dist) * step;
            m.y += (dy / dist) * step;
            tries++;
        }
        placed.push(m);
        const odx = Math.round(m.x - m.ox);
        const ody = Math.round(m.y - m.oy);
        if (odx !== 0 || ody !== 0) offsets[m.id] = {x: odx, y: ody};
    });
};
function recomputeCollisions() {
    if (!COLLISION_ENABLED) {
        markerOffsets.value = {};
        return;
    }
    const lmap = map.value?.leafletObject;
    if (!lmap) return;
    try {
        const list = [];
        (devicesWithFollowed.value || []).forEach((d) => {
            if (d.lat == null || d.lng == null) return;
            const pt = lmap.latLngToContainerPoint([d.lat, d.lng]);
            list.push({id: d.id, x: pt.x, y: pt.y, ox: pt.x, oy: pt.y, priority: markerPriority(d), moving: !!d.motion_state});
        });
        const n = list.length;
        const seen = new Array(n).fill(false);
        const offsets = {};
        for (let i = 0; i < n; i++) {
            if (seen[i]) continue;
            const queue = [i];
            seen[i] = true;
            const group = [];
            while (queue.length) {
                const c = queue.pop();
                group.push(list[c]);
                for (let j = 0; j < n; j++) {
                    if (!seen[j] && Math.hypot(list[c].x - list[j].x, list[c].y - list[j].y) < COLLISION_MIN_DIST) {
                        seen[j] = true;
                        queue.push(j);
                    }
                }
            }
            if (group.length > 1) declutterGroup(group, offsets);
        }
        markerOffsets.value = offsets;
    } catch (e) {
        markerOffsets.value = {};
    }
}

const mapIsReady = () => {
    mapState.value = true;
    map.value.leafletObject.attributionControl.remove();
    if (locationPolygons.value) {
        map.value.leafletObject.addLayer(locationPolygons.value);
    }
    // While panning/zooming keep markers at their raw GPS points (no offset
    // churn); recompute once the map settles.
    map.value.leafletObject.on('movestart zoomstart', () => {
        mapMoving = true;
        markerOffsets.value = {};
    });
    map.value.leafletObject.on('moveend zoomend', () => {
        mapMoving = false;
        scheduleCollision();
    });
    scheduleCollision();
}
const drawPolygon = async (location) => {
    if (map.value && locationPolygons.value) {
        const cords = location.polygon_coordinates.map((coordinate) => {
            return {'lat': coordinate.latitude, 'lng': coordinate.longitude}
        });
        L.polygon(cords, {id: location.id, name: location.name}).addTo(locationPolygons.value)
    }
}

// Draw the zones/spots overlay payload ({zones, spots}) into its feature group.
const drawZonesOverlay = (data) => {
    if (!zonesOverlay.value) return;
    zonesOverlay.value.clearLayers();
    (data?.zones || []).forEach((zone) => {
        const coords = (zone.polygon_coordinates || []).map((c) => [c.latitude, c.longitude]);
        if (coords.length < 3) return;
        const fill = ZONE_OVERLAY_COLORS[zone.type ?? 0] || '#607D8B';
        L.polygon(coords, {color: fill, weight: 2, fillColor: fill, fillOpacity: 0.18})
            .bindTooltip(zone.name ?? 'Zone', {sticky: true})
            .addTo(zonesOverlay.value);
    });
    (data?.spots || []).forEach((spot) => {
        const coords = (spot.polygon_coordinates || []).map((c) => [c.latitude, c.longitude]);
        if (coords.length < 3) return;
        L.polygon(coords, {color: '#455A64', weight: 1, dashArray: '3', fillColor: '#90A4AE', fillOpacity: 0.12})
            .bindTooltip(spot.name ?? 'Spot', {sticky: true})
            .addTo(zonesOverlay.value);
    });
};

const loadZonesOverlay = async (force = false) => {
    const companyId = user?.company?.id;
    if (!companyId || zonesOverlayLoading) return;
    zonesOverlayLoading = true;
    try {
        const response = await send('get', `companies/${companyId}/zones-overlay`);
        const data = response?.data?.data ?? response?.data ?? {};
        // Skip the redraw when the server reports nothing changed since last draw.
        if (!force && zonesOverlayLoadedAt.value && data.zones_updated_at === zonesOverlayLoadedAt.value) return;
        drawZonesOverlay(data);
        zonesOverlayLoadedAt.value = data.zones_updated_at ?? null;
    } catch (e) {
        console.log('zones-overlay load failed', e);
    } finally {
        zonesOverlayLoading = false;
    }
};

const toggleZonesOverlay = () => {
    showZonesOverlay.value = !showZonesOverlay.value;
    const lmap = map.value?.leafletObject;
    if (!lmap) return;
    if (showZonesOverlay.value) {
        lmap.addLayer(zonesOverlay.value);
        loadZonesOverlay();
    } else {
        lmap.removeLayer(zonesOverlay.value);
    }
};

const isMarkerInsidePolygon = (device, poly) => {
    return poly.getBounds().contains(L.latLng(device.lat, device.lng));
}
const updateDeviceLocation = (deviceID, locationID = null) => {
    if (user && user.type === 'company') {
        send("put", `devices/location`, {
            device: deviceID,
            location: locationID
        }).catch((errorResponse) => console.log(errorResponse));
    }
}
const validateMarkers = () => {
    const polygons = Object.values(locationPolygons?.value?._layers)
    if (!polygons.length || !markers.value.length) return;

    // Only check devices that have been flagged as location_updated
    const updatedDevices = [];
    props.devices.forEach((device, index) => {
        if (device.location_updated) updatedDevices.push({ device, index });
    });
    if (!updatedDevices.length) return;

    for (const { device, index } of updatedDevices) {
        for (const polygon of polygons) {
            if (isMarkerInsidePolygon(device, polygon)) {
                props.devices[index]['location'] = `At ${polygon.options.name}`;
                updateDeviceLocation(device.id, polygon.options.id);
                break; // device can only be in one location
            }
        }
        props.devices[index]['location_updated'] = false;
    }
}

watch(() => props.companyLocations, (locations) => {
    if (user && user.type !== 'company') {
        return;
    }

    companyLocations.value = locations;
    if (companyLocations.value.length) {
        companyLocations.value.forEach(location => {
            if (location.polygon_coordinates.length) {
                drawPolygon(location);
            }
        });
        validateMarkers();
    }
});

watch(() => props.selectedDevice, async (device) => {
    if (device) {
        selectedMarker.value = device;
        const marker = getDeviceMarker(device.id);
        if (marker?.leafletObject) marker.leafletObject.openPopup();
        // Never geocode an activation seed: the point is where it will be, not
        // where it reported from, and persisting an address for it (the PUT
        // below) would stamp a real-looking location on a device with no fix.
        if (!isApproximate(device) && hasMappableCoords(device) && !isValidAddress(device.formatted_address)) {
            await geocodeLatLng({'lat': device.lat, 'lon': device.lng})
                .then((response) => {
                        if (!isValidAddress(response)) return;
                        let deviceIndex = props.devices.findIndex((item) => item.slno === device.slno);
                        props.devices[deviceIndex]['formatted_address'] = response;
                        selectedMarker.value['formatted_address'] = response;
                        updateGeocodedAddress(response)
                    }
                )
        }
    }
});

watch(() => props.zoomToDevice, (device) => {
    const marker = getDeviceMarker(device.id);
    if (map.value?.leafletObject && marker) {
        map.value.leafletObject.setView(marker.latLng, 18);
    }
});

watch(() => props.nearestToDevice, (device) => {
    const marker = getDeviceMarker(device.id);
    if (marker) findNearestMarker(marker);
});

watch(() => props.devices, (devices) => {
    props.devices.forEach((device) => {
        if (!device.motion_state) {
            device.isHighlighted = false;
        }
    });
    bounds.value = props.devices
        .filter(isShownOnMap)
        .map((device) => [device.lat, device.lng]);
    if (bounds.value.length && map.value?.leafletObject) {
        map.value.leafletObject.fitBounds(L.latLngBounds(bounds.value), {padding: [100, 100]});
    }
    validateMarkers();
});

// Recompute the declutter offsets whenever the rendered device set changes.
watch(devicesWithFollowed, scheduleCollision);

watch(lastMqttUpdate, async (update) => {
    if (!update) return;
    const slno = update.slno;
    const location = update.data;

    // Search in allDevices so followed-but-filtered devices still get MQTT updates
    const searchList = props.allDevices || props.devices;
    let deviceIndex = searchList.findIndex((device) => device.slno === slno);

    if (deviceIndex > -1) {
        // Play beep sounds for followed devices on motion state transitions
        if (trackedDeviceIds.value.has(searchList[deviceIndex].id)) {
            const oldMotionState = searchList[deviceIndex]['motion_state'];
            const msgType = location.message_type;
            if (msgType === mqttMessageTypes['MOTION_STARTED'] && oldMotionState === 0) {
                playMotionStarted();
            } else if (msgType === mqttMessageTypes['MOTION_IN_PROGRESS']) {
                playMotionChirp();
            } else if (msgType === mqttMessageTypes['MOTION_ENDED'] && oldMotionState === 1) {
                playMotionEnded();
            }
        }

        // Only update motion state for known motion message types (2=started, 3=in_progress, 4=ended)
        if ([mqttMessageTypes.MOTION_STARTED, mqttMessageTypes.MOTION_IN_PROGRESS, mqttMessageTypes.MOTION_ENDED].includes(location.message_type)) {
            searchList[deviceIndex]['motion_state'] = location.message_type === mqttMessageTypes['MOTION_ENDED'] ? 0 : 1;
            searchList[deviceIndex]['isHighlighted'] = location.message_type === mqttMessageTypes['MOTION_ENDED'] ? 0 : 1;
        }
        // Propagate motion boundary timestamps so motionLabel() can show
        // "In Motion for Xm" / "Parked for Xh". Gateway sends these fields
        // once its own deploy lands; until then, fall back to the client
        // clock at the moment the transition MQTT packet arrives. Devices
        // already in a state at page-load show no duration (unknown start).
        const nowIso = new Date().toISOString();
        if (location.motion_started_at !== undefined) {
            searchList[deviceIndex]['motion_started_at'] = location.motion_started_at;
            searchList[deviceIndex]['motion_ended_at'] = null;
        } else if (location.message_type === mqttMessageTypes.MOTION_STARTED) {
            searchList[deviceIndex]['motion_started_at'] = nowIso;
            searchList[deviceIndex]['motion_ended_at'] = null;
        }
        if (location.motion_ended_at !== undefined) {
            searchList[deviceIndex]['motion_ended_at'] = location.motion_ended_at;
            searchList[deviceIndex]['motion_started_at'] = null;
        } else if (location.message_type === mqttMessageTypes.MOTION_ENDED) {
            searchList[deviceIndex]['motion_ended_at'] = nowIso;
            searchList[deviceIndex]['motion_started_at'] = null;
        }
        // Clear API fleet_status so composable uses client-side logic for real-time data
        delete searchList[deviceIndex]['fleet_status'];

        // Always update timestamps and voltage/signal/battery
        searchList[deviceIndex]['last_updated_ago'] = location.last_updated_ago;
        searchList[deviceIndex]['last_message_received'] = location.last_message_received;
        // Select the voltage the SKU reports on, with the same precedence as
        // DeviceLiveViewResource 'voltage' and the gateway's battery_bars:
        // channel 1 if battery1_status, else channel 2 if battery2_status,
        // else channel 1. (obd_present is not a channel selector; today's OBD
        // SKUs are battery2-only so this is the same pick, but an OBD SKU set
        // to channel 1 must follow its flags like the server does.)
        const dev = searchList[deviceIndex];
        searchList[deviceIndex]['voltage'] = dev.battery1_status ? location.voltage1
            : (dev.battery2_status ? location.voltage2 : location.voltage1);
        searchList[deviceIndex]['voltage2'] = location.voltage2;
        searchList[deviceIndex]['signal_bars'] = location.signal_bars;
        searchList[deviceIndex]['battery_bars'] = location.battery_bars;
        if (location.driver) {
            searchList[deviceIndex]['driver_photo'] = location.driver['driver_photo'];
            searchList[deviceIndex]['driver_last_name'] = location.driver['driver_last_name'];
            searchList[deviceIndex]['driver_first_name'] = location.driver['driver_first_name'];
        }

        // Only update position/speed/heading when GPS fix is valid
        if (location.valid !== false) {
            // A real fix replaces the activation seed: stop fading this device,
            // but ONLY when the packet actually carries usable coordinates, so a
            // coordless/malformed "valid" message can't un-fade it (or, via the
            // else branch below, strand it at 0,0). The geocode below then runs
            // on the real position.
            const _seedLat = Number(location.lat);
            const _seedLng = Number(location.lng);
            if (Number.isFinite(_seedLat) && Number.isFinite(_seedLng) &&
                !(Math.abs(_seedLat) < 0.0001 && Math.abs(_seedLng) < 0.0001)) {
                searchList[deviceIndex]['approximate'] = false;
            }
            // Animate position change instead of instant snap
            const oldLat = searchList[deviceIndex].lat;
            const oldLng = searchList[deviceIndex].lng;
            if (oldLat && oldLng && location.lat && location.lng) {
                const dist = L.latLng(oldLat, oldLng).distanceTo(L.latLng(location.lat, location.lng));
                if (dist > 3) {
                    startDeviceAnimation(slno, oldLat, oldLng, location.lat, location.lng, deviceIndex);
                } else {
                    // Number(), not the raw payload value. Postgres numerics and
                    // some publishers put lat/lng on the wire as STRINGS, and a
                    // string here is invisible until something adds to it: the
                    // marker tween then produces "34.0785081-0.00022" instead of
                    // a coordinate, and every consumer of that device throws.
                    searchList[deviceIndex].lat = Number(location.lat);
                    searchList[deviceIndex].lng = Number(location.lng);
                }
            } else {
                searchList[deviceIndex].lat = Number(location.lat);
                searchList[deviceIndex].lng = Number(location.lng);
            }
            searchList[deviceIndex]['heading'] = location.heading;
            searchList[deviceIndex]['location'] = `${location.lat}, ${location.lng}`;
            searchList[deviceIndex]['gps_speed_raw'] = Number(location.gps_speed) || 0;
            searchList[deviceIndex]['gps_speed'] = strOperations().unitifyDistance(location.gps_speed);
            // The gateway now publishes a converted speed alongside the raw one,
            // because the raw field carries MPH from hardware and km/h x2 from
            // phones and MQTT never passes through the API. Carry it onto the
            // device so the display sites read a live value rather than a stale
            // REST one. Absent on an un-migrated gateway, in which case the
            // display falls back to the raw number exactly as before.
            // Assign unconditionally. Assigning only when present would leave the
            // REST row's speed_mph pinned at its page-load value while gps_speed
            // kept ticking, freezing every display site until the gateway ships.
            // Nulling it here re-engages the raw fallback instead.
            searchList[deviceIndex]['speed_mph'] =
                location.speed_mph === undefined || location.speed_mph === null
                    ? null
                    : Number(location.speed_mph);

            // Geocode for selected marker popup
            if (selectedMarker.value?.slno === slno) {
                await geocodeLatLng({'lat': location.lat, 'lon': location.lng}).then((response) => {
                        if (isValidAddress(response)) {
                            searchList[deviceIndex]['location'] = response;
                            searchList[deviceIndex]['formatted_address'] = response;
                        }
                    }
                )
            }
        }
        // Geocode and auto-adjust only when GPS is valid
        if (location.valid !== false) {
            // Geocode for followed devices
            if (trackedDeviceIds.value.has(searchList[deviceIndex].id)) {
                geocodeFollowedDevice(slno, location.lat, location.lng);
            }
            // Auto-adjust for tracked devices
            if (trackedDeviceIds.value.has(searchList[deviceIndex].id) && map.value?.leafletObject) {
                const firstTrackedId = [...trackedDeviceIds.value][0];
                if (searchList[deviceIndex].id === firstTrackedId) {
                    map.value.leafletObject.panTo(
                        [location.lat, location.lng],
                        { animate: true, duration: 0.45, easeLinearity: 0.3 }
                    );
                } else {
                    const currentBounds = map.value.leafletObject.getBounds();
                    const deviceLatLng = L.latLng(location.lat, location.lng);
                    if (!currentBounds.contains(deviceLatLng)) {
                        fitTrackedDevices();
                    }
                }
            }
        }
    }
});

const fitAllDevices = () => {
    const latLngs = props.devices
        .filter(isShownOnMap)
        .map(d => [d.lat, d.lng]);
    if (latLngs.length && map.value?.leafletObject) {
        map.value.leafletObject.fitBounds(L.latLngBounds(latLngs), {padding: [100, 100]});
    }
};

defineExpose({fitAllDevices});

onBeforeUnmount(() => {
    clearInterval(lastMessageCheckTimer);
    if (animationTimer) clearInterval(animationTimer);
    if (trailCleanupTimer) clearInterval(trailCleanupTimer);
    if (timeAgoTimer) clearInterval(timeAgoTimer);
    for (const trail of deviceTrails.values()) {
        trail.polyline?.remove();
    }
    deviceTrails.clear();
    deviceAnimations.clear();
    disposeBeepSounds();
})
</script>

<style>
.marker .pulse {
    /*background: #d6d4d4;*/
    border-radius: 50%;
    height: 14px;
    width: 14px;
    position: absolute;
    left: 50%;
    top: 50%;
    margin: 11px 0 0 -12px;
    transform: rotateX(55deg);
    z-index: -2;
}

.marker .pulse:after {
    opacity: 0;
    content: "";
    width: 40px;
    height: 40px;
    border-radius: 50%;
    position: absolute;
    margin: -20px 0 0 -15px;
    animation: pulsate 1s ease-out;
    box-shadow: 0 0 1px 2px #cb027e;
    animation-iteration-count: infinite;
}

@keyframes pulsate {
    0% {
        opacity: 0;
        transform: scale(0.1, 0.1);
    }

    50% {
        opacity: 1;
    }

    100% {
        opacity: 0;
        transform: scale(1.2, 1.2);
    }
}

.tracking-ring {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 40px;
    height: 40px;
    margin: -10px 0 0 -20px;
    border-radius: 50%;
    border: 3px solid #1565C0;
    opacity: 0;
    z-index: -1;
    animation: trackingPulse 2s ease-out infinite;
}

@keyframes trackingPulse {
    0% {
        opacity: 0.8;
        transform: scale(0.5);
    }
    100% {
        opacity: 0;
        transform: scale(1.8);
    }
}

.asset-name-label {
    color: #ffca28;
    font-size: 9px;
    font-weight: 600;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.2;
}

.live-badge {
    background: #e53935;
    color: white;
    font-size: 7px;
    font-weight: bold;
    padding: 1px 4px;
    border-radius: 3px;
    margin-right: 4px;
    vertical-align: middle;
}

.address-label-content {
    background: rgba(0, 0, 0, 0.80);
    border-radius: 6px;
    padding: 4px 8px;
    text-align: center;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
    white-space: nowrap;
    max-width: 230px;
}

.speed-info {
    font-size: 10px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
}

.address-text {
    color: white;
    font-size: 9px;
    font-weight: 400;
    line-height: 1.2;
    overflow: hidden;
    text-overflow: ellipsis;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
}

.copy-address-icon {
    cursor: pointer;
    opacity: 0.8;
    flex-shrink: 0;
}

.copy-address-icon:hover {
    opacity: 1;
}

.time-ago {
    opacity: 0.7;
    font-weight: 400;
    font-size: 9px;
}

.following-panel {
    position: absolute;
    top: 120px;
    left: 8px;
    z-index: 1001;
}

.following-list {
    background: rgba(255, 255, 255, 0.95);
    border-radius: 8px;
    padding: 8px;
    margin-top: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    max-height: 200px;
    overflow-y: auto;
    min-width: 180px;
}

.following-item {
    display: flex;
    flex-direction: column;
    padding: 4px 0;
    font-size: 12px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.following-item:last-child {
    border-bottom: none;
}

.following-item-header {
    display: flex;
    align-items: center;
    gap: 6px;
}

.following-address {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 10px;
    color: #666;
    padding-left: 20px;
    margin-top: 2px;
}

.following-label {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.unfollow-btn {
    cursor: pointer;
    opacity: 0.4;
    flex-shrink: 0;
}

.unfollow-btn:hover {
    opacity: 1;
    color: red !important;
}

/* --- Popup redesign styles --- */
.popup-container {
    font-size: 13px;
    line-height: 1.4;
}

.popup-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}

.popup-photo-wrapper {
    position: relative;
    flex-shrink: 0;
}

.popup-photo {
    flex-shrink: 0;
    border: 1px solid #e0e0e0;
}

.popup-driver-overlay {
    position: absolute;
    right: -4px;
    bottom: -4px;
    border: 2px solid #fff;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.popup-header-info {
    flex: 1;
    min-width: 0;
}

.popup-asset-label {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    text-decoration: none;
    text-transform: capitalize;
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.popup-asset-label:hover {
    color: #1565C0;
}

.popup-subtitle {
    font-size: 11px;
    color: #999;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.popup-header-right {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-shrink: 0;
}

.popup-status-strip {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 8px;
    padding: 4px 8px;
    background: #f5f5f5;
    border-radius: 6px;
}

.popup-motion-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 600;
    color: white;
    white-space: nowrap;
    flex-shrink: 0;
}

.popup-motion-active {
    background: #1976D2;
}

.popup-motion-idle {
    background: #EF6C00;
}

.popup-motion-parked {
    background: #9E9E9E;
}

.popup-motion-offline {
    background: #B91C1C;
}

.popup-speed {
    font-size: 12px;
    font-weight: 600;
    color: #333;
    white-space: nowrap;
}

.popup-status-icons {
    margin-left: auto;
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
}

.popup-voltage {
    font-size: 11px;
    color: #666;
    margin-left: 2px;
}

.popup-obd-chip {
    font-size: 9px !important;
    height: 16px !important;
    padding: 0 4px !important;
}

.popup-info-row {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 4px;
    font-size: 12px;
    color: #444;
}

.popup-location-row {
    align-items: flex-start;
}

.popup-location-text {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
}

.popup-zone-name {
    font-weight: 600;
    color: #2e7d32;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}


.popup-location-address {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    word-break: break-word;
}


.popup-location-actions {
    display: flex;
    gap: 4px;
    flex-shrink: 0;
    margin-top: 1px;
}

.popup-inline-icon {
    cursor: pointer;
    opacity: 0.5;
    color: #666;
}

.popup-inline-icon:hover {
    opacity: 1;
    color: #1565C0;
}

.popup-update-row {
    gap: 4px;
}

.popup-update-row a {
    display: inline-flex;
}

.popup-actions {
    display: flex;
    justify-content: space-around;
    padding-top: 4px;
}

.popup-action-btn {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 3px;
    cursor: pointer;
}

.popup-action-circle {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.15s;
}

.popup-action-circle:hover {
    opacity: 0.85;
}

.popup-action-label {
    font-size: 10px;
    color: #666;
    white-space: nowrap;
}

</style>
